/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-16 11:51:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { v4 } from 'uuid';

import { Icon, TextField, TextStyle } from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { FullscreenBar } from 'src/components/views/common/fullscreenBar';

import FeaturePreview from 'src/components/containers/test-your-search/feature-preview';
import ProductPreview from 'src/components/containers/test-your-search/products-preview';
import listSearchIcon from 'src/assets/images/common/list-search-icon.png';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import TitleHelp from 'src/components/views/common/title-help';
import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';
import PremiumLockBanner from 'src/components/views/test-your-search/premium-lock-banner';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';
import { routes } from 'src/routes';
import { handleOpenFullscreen } from 'actions/app-integration-actions';
import { buildQueriesForSearchFeatures } from 'src/helpers/search-helper';
import { checkUseSearchPersonalization } from 'src/helpers/subscription-v3-helper';
import { removeExtraSpace } from 'src/helpers';
import { URL } from 'src/constants/links';
import { ADDONS_INFO, AI_POWERED_SEARCH_NAME } from 'src/constants/subscription';

import history from 'src/routes/history';
import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import { ComposedProps } from './index.type';
import * as productAction from 'src/stores/actions/product-actions';
import * as searchPreviewAction from 'src/stores/actions/search-preview';
import * as filterSettingsActions from 'actions/filter-settings-actions';
import * as productPerformanceRankingAction from 'actions/product-performance-ranking-action';
import * as personalizationAction from 'actions/search-personalization-action';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import * as SemanticActions from 'actions/semantic-action';

const NUMBER_PRODUCTS_PER_PAGE = 18;

const TestYourSearch = (props: ComposedProps) => {
  const {
    i18n,
    filterSettings,
    getFilterSettings,
    handleOpenFullscreen,
    getProducts,
    searchFeaturesPreview,
    searchFeatures,
    tenantId,
    isUsePersonalization,
    pprSettings,
    getPPRSettings,
    personalizationSettings,
    getPersonalizationSettings,
    enableAISearch,
    useBoltOn,
    loadingProduct,
    loadingFeatures,
    invoice,
    getAddonStatus,
    getDetailInvoice,
    addonStatus,
    statusStore,
    notAllowAddon,
    triggerCheckSyncSemantic,
    getSemanticSettings
  } = props;

  const [query, setQuery] = useState('');
  const [openVideo, setOpenVideo] = useState(false);
  const [term, setTerm] = useState('');
  const [showLoading, setShowLoading] = useState(false);
  const [modalAddon, setModalAddon] = React.useState(false);
  const [changePlanFromReviewIOModal, setChangePlanFromReviewIOModal] = useState(false);

  const addonRemainingCycles = _.get(addonStatus, 'trialInfo.remainingDays', 14);

  const isSubscribeNow =
    !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0;

  // Handle action opening the visual editor
  const handleOpenFullscreenPage = () => {
    const unsubscribeExit = enterSABFullscreen(handleCloseFullscreenPage);
    handleOpenFullscreen(true);

    return unsubscribeExit;
  };

  // Handle action closing the fullscreen
  const handleCloseFullscreenPage = () => {
    exitSABFullscreen();
    history.push(routes.relevanceSettings.view());
    handleOpenFullscreen(false);
  };

  // Build queries get products
  const buildQueries = currentPage => {
    const sortingAvailableFirst = _.get(filterSettings, 'general.sortingAvailableFirst', false);
    const availableAfterFiltering = _.get(filterSettings, 'general.availableAfterFiltering', false);
    const productAndVariantAvailable = _.get(
      filterSettings,
      'general.productAndVariantAvailable',
      false
    );
    const queries = {
      shop: tenantId,
      t: Date.now(),
      q: removeExtraSpace(query),
      sort: 'relevance',
      collection_scope: 0,
      product_available: availableAfterFiltering || productAndVariantAvailable,
      variant_available: productAndVariantAvailable,
      event_type: 'init',
      page: currentPage,
      limit: NUMBER_PRODUCTS_PER_PAGE,
      reset_cache: true,
      sid: v4(),
      rid: v4()
    };
    if (sortingAvailableFirst) {
      _.set(queries, 'sort_first', 'available');
    }
    return queries;
  };

  // on enter query
  const handleKeyPress = event => {
    const enterKeyPressed = event?.keyCode === 13;
    if (enterKeyPressed) {
      getData();
      return;
    }
  };
  // Get data search features and products by queries
  const getData = async () => {
    let testSearchData = {};
    const queries = buildQueries(1);
    setShowLoading(true);
    const productData = await getProducts(queries);
    if (productData?.type === 'SEARCH_PRODUCTS_SUCCESS') {
      testSearchData = _.get(productData, 'payload.test_search_data', {});
    }
    const { featuresQueries, term } = buildQueriesForSearchFeatures(
      query,
      enableAISearch,
      testSearchData
    );
    setTerm(term);
    await searchFeaturesPreview(featuresQueries);
  };

  // Before render
  useEffect(() => {
    const unsubscribeExit = handleOpenFullscreenPage();
    // Get current addon status
    getAddonStatus(AI_POWERED_SEARCH_NAME);
    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }
    // get filter settings
    if (_.isEmpty(filterSettings)) {
      getFilterSettings();
    }
    getData();

    if (_.isEmpty(pprSettings)) getPPRSettings();
    if (isUsePersonalization && _.isEmpty(personalizationSettings)) getPersonalizationSettings();

    return () => {
      unsubscribeExit();
    };
  }, []);

  // When re-call
  useEffect(() => {
    if (loadingFeatures === false && loadingProduct === false) {
      setShowLoading(false);
      const redirects = _.get(searchFeatures, 'redirects', {});
      if (!_.isEmpty(redirects)) setTerm(query);
    } else setShowLoading(true);
  }, [loadingFeatures, loadingProduct]);

  useEffect(() => {
    if (!query || query === '') {
      getData();
    }
  }, [query, filterSettings]);

  const enablePPR = _.get(pprSettings, 'enabled') || false;
  const enablePersonalization = _.get(personalizationSettings, 'enabled') || false;

  const onClickLockCard = () => {
    if (notAllowAddon) return;

    if (useBoltOn) setChangePlanFromReviewIOModal(!changePlanFromReviewIOModal);
    else setModalAddon(!modalAddon);
  };

  /** Subscribe addon - reload page*/
  const handleCloseStatus = () => {
    getAddonStatus(AI_POWERED_SEARCH_NAME);
    getSemanticSettings();
    triggerCheckSyncSemantic();
  };

  return (
    <div className="BC-Page BC-Page__TestYourSearch">
      <FullscreenBar
        onAction={() => handleCloseFullscreenPage()}
        backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
        title={i18n.translate(`AdminHTML.TestYourSearch.TestYourSearch.title`)}
      />
      <div className="BC-TestYourSearch-Card">
        {!enableAISearch && (
          <PremiumLockBanner
            disableLockUnLock={notAllowAddon}
            isSubscribeNow={isSubscribeNow}
            action={onClickLockCard}
          />
        )}
        <div className="text-field-query">
          <div>
            <img alt="list-search-icon" src={listSearchIcon} width={24} height={24} />
            <p>{i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.description')}</p>
          </div>
          <TitleHelp
            i18n={i18n}
            onPlay={() => setOpenVideo(true)}
            hrefUserManual={URL['Link_Test_Your_Search_Guide']}
          />
          <div>
            <div onKeyDown={handleKeyPress}>
              <TextField
                onChange={value => setQuery(value)}
                id="queryInput"
                value={query}
                clearButton
                onClearButtonClick={() => setQuery('')}
                prefix={<Icon source={SearchMinor} color="base" />}
                placeholder={i18n.translate(
                  'AdminHTML.TestYourSearch.TestYourSearch.textField_Placeholder'
                )}
              />
            </div>
          </div>

          <p style={{ color: '#6D7175' }}>
            <span>
              {i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.textField_Content', {
                t: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.textField_Content_t')}
                  </TextStyle>
                )
              })}
            </span>
            {isUsePersonalization && enablePersonalization && (
              <>
                <br />
                <span>
                  {i18n.translate(`AdminHTML.TestYourSearch.TestYourSearch.textField_Content2`)}
                </span>
              </>
            )}
          </p>
        </div>
        <FeaturePreview query={term} showLoading={showLoading} />
        <div className="test-your-search-divider" />
        <ProductPreview
          query={term}
          showLoading={showLoading}
          getProducts={getProducts}
          buildQueries={buildQueries}
          enablePPR={enablePPR}
        />
      </div>
      <YoutubeModal
        active={openVideo}
        embeddedURL={URL['Link_Test_Your_Search_Video']}
        onClose={() => setOpenVideo(false)}
        height={415}
        title={i18n.translate('AdminHTML.MerchandisingV2.Merchandising.youtubeModal_Title')}
      />
      {/* Process addon unlock */}
      <ProcessAddon
        openModalReview={modalAddon}
        addon={ADDONS_INFO.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
        hadSubscribed={isSubscribeNow}
        handleAfterSubscribeAddon={handleCloseStatus}
        handleReviewModal={() => setModalAddon(!modalAddon)}
      />
      <ChangePlanFromReviewIOModal
        open={changePlanFromReviewIOModal}
        onClose={() => setChangePlanFromReviewIOModal(false)}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterSettings: () =>
    dispatch(filterSettingsActions.getFilterSettings(undefined, ownProps.i18n)),
  searchFeaturesPreview: (queries: Object) =>
    dispatch(searchPreviewAction.searchFeaturesPreview(queries)),
  getProducts: (queries: Object) => dispatch(productAction.searchProducts(queries)),
  handleOpenFullscreen: (isOpenFullScreen: boolean) =>
    dispatch(handleOpenFullscreen(isOpenFullScreen)),
  getPPRSettings: () => dispatch(productPerformanceRankingAction.getSettings()),
  getPersonalizationSettings: () => dispatch(personalizationAction.getSettings()),
  // Get current status addon sync
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
  getSemanticSettings: () => dispatch(SemanticActions.getSemanticSettings()),
  triggerCheckSyncSemantic: () => dispatch(SemanticActions.triggerCheckSyncSemantic())
});
const mapStateToProps = state => {
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  const pprSettings = _.get(state.productPerformanceRanking, 'settings') || {};
  const personalizationSettings = _.get(state.searchPersonalization, 'settings') || {};
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const isUsePersonalization = checkUseSearchPersonalization(tenantApp);

  return {
    filterSettings: _.get(state, 'filterSettings.filterSettings', {}),
    tenantId,
    isUsePersonalization,
    pprSettings,
    personalizationSettings,
    loadingProduct: _.get(state.product, 'loading', false),
    loadingFeatures: _.get(state.searchPreview, 'loading', false),
    searchFeatures: _.get(state.searchPreview, 'features', {}),
    enableAISearch: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.AI-synonyms',
      false
    ),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false),
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    addonStatus: _.get(state.subscriptionV3, `addonStatus.${AI_POWERED_SEARCH_NAME}`, {})
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(TestYourSearch));
